import {
  DateInput,
  FileInput,
  Modal,
  NumberInput,
  SelectInput,
  TextField,
} from 'melp-design/components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { Stack } from '@mui/material';
import { useAdminCard } from 'store/admin-cards';
import { useImportVouchersFiles } from 'store/admin-card-vouchers';
import { formatCurrency } from 'utils/format';
import { APP } from 'config';

interface Values {
  cardPrice: number;
  purchasePrice: number;
  purchaseReference: string;
  expiresAt: string | null;
  files: File[];
}

const INITIAL_VALUES: Values = {
  cardPrice: NaN,
  purchasePrice: NaN,
  purchaseReference: '',
  expiresAt: null,
  files: [],
};

interface Props extends ModalProps {
  cardId: string;
}

export const UploadVoucherFilesModal: FC<Props> = ({ cardId, closeModal }) => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const { data: card } = useAdminCard(cardId);

  const { mutate: importVouchersFiles, isLoading: isImporting } =
    useImportVouchersFiles();

  const onSubmit: SubmitHandler<Values> = (values) => {
    if (!card) return;

    importVouchersFiles(
      {
        cardId,
        cardPrice: values.cardPrice,
        cardCurrency: card.faceValueCurrency,
        files: values.files,
        purchaseReference: values.purchaseReference,
        purchasePrice: values.purchasePrice,
        expiresAt: values.expiresAt,
      },
      {
        onSuccess: () => {
          predefinedToasts.success.added();
          closeModal({ action: 'CLOSE' });
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('global.actions.upload_filetypes', {
          fileTypes: ['.pdf', '.png'].join(', '),
        })}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: isImporting,
          },
        ]}
      >
        <Stack gap="20px">
          <Controller
            name="cardPrice"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SelectInput
                label={t('marketplace.ordersTablePrice')}
                name={name}
                value={value}
                onChange={onChange}
                options={
                  card?.prices.map((price) => ({
                    label: formatCurrency(
                      i18n.language,
                      price.faceValue,
                      card.faceValueCurrency,
                    ),
                    value: price.faceValue,
                  })) ?? []
                }
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="purchasePrice"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <NumberInput
                label={t('shop.vouchers.purchasePrice.label')}
                name={name}
                value={value}
                onChange={onChange}
                required
                key={value}
                InputProps={{
                  endAdornment: APP.currencies.default,
                }}
                ref={ref}
              />
            )}
          />

          <Controller
            name="purchaseReference"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <TextField
                  label={t('shop.vouchers.purchaseReference.label')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="expiresAt"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <DateInput
                label={t('alternatives.employeeExpirationDate')}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="files"
            control={control}
            rules={{ required: true }}
            render={({ field: { name, onChange, ref } }) => (
              <FileInput
                name={name}
                onChange={onChange}
                accept={['pdf', 'png']}
                maxFiles={Infinity}
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
